import React, { useCallback, useEffect, useRef } from "react";
import { Trash2 } from "lucide-react";
import { DEFAULT_STICKY, STICKY_BG, STICKY_BG_SOLID, STICKY_COLORS } from "../../lib/boardConstants";
import { cn } from "../../lib/utils";

function stopBubble(event) {
  event.stopPropagation();
}

export function BoardSticky({
  element,
  selected,
  onSelect,
  onChange,
  onDelete,
  zoom,
}) {
  const dragRef = useRef(null);
  const textareaRef = useRef(null);

  const onDragPointerDown = useCallback(
    (event) => {
      event.stopPropagation();
      event.preventDefault();
      onSelect(element.id);

      const startX = event.clientX;
      const startY = event.clientY;
      const originX = element.x;
      const originY = element.y;

      dragRef.current = { startX, startY, originX, originY };

      const onMove = (moveEvent) => {
        if (!dragRef.current) return;
        if (moveEvent.cancelable) moveEvent.preventDefault();
        const dx = (moveEvent.clientX - dragRef.current.startX) / zoom;
        const dy = (moveEvent.clientY - dragRef.current.startY) / zoom;
        onChange(element.id, {
          x: dragRef.current.originX + dx,
          y: dragRef.current.originY + dy,
        });
      };

      const onUp = () => {
        dragRef.current = null;
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onUp);
      };

      window.addEventListener("pointermove", onMove, { passive: false });
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onUp);
    },
    [element.id, element.x, element.y, onChange, onSelect, zoom],
  );

  useEffect(() => {
    if (selected && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [selected]);

  const bg = STICKY_BG[element.color] || STICKY_BG.yellow;
  const width = element.width || DEFAULT_STICKY.width;
  const height = element.height || DEFAULT_STICKY.height;

  return (
    <div
      className={cn(
        "absolute flex touch-none flex-col overflow-hidden rounded-lg shadow-[0_10px_28px_-10px_rgba(0,0,0,0.18),inset_0_1px_0_rgba(255,255,255,0.72),inset_0_-1px_0_rgba(0,0,0,0.04)] ring-1 ring-black/[0.06] backdrop-blur-[1px]",
        selected && "ring-2 ring-[var(--t-accent)] ring-offset-1 ring-offset-transparent",
      )}
      style={{
        left: element.x,
        top: element.y,
        width,
        minHeight: height,
        height: selected ? "auto" : height,
        zIndex: element.zIndex || 0,
        backgroundColor: bg,
      }}
      onPointerDown={(event) => {
        event.stopPropagation();
        onSelect(element.id);
      }}
      data-testid={`board-sticky-${element.id}`}
    >
      <div
        className="pointer-events-none absolute inset-0 rounded-lg"
        style={{
          background:
            "linear-gradient(155deg, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.14) 36%, transparent 58%), linear-gradient(325deg, rgba(255,255,255,0.2) 0%, transparent 52%)",
        }}
        aria-hidden
      />
      <div
        data-sticky-drag-handle
        className="relative z-[1] flex h-5 shrink-0 cursor-grab items-center justify-center border-b border-black/[0.06] active:cursor-grabbing"
        onPointerDown={onDragPointerDown}
        aria-label="Drag sticky note"
      >
        <span className="h-1 w-8 rounded-full bg-black/10" aria-hidden />
      </div>
      {selected ? (
        <div
          data-sticky-toolbar
          className="relative z-[1] flex items-center gap-1.5 border-b border-black/[0.06] px-2 py-1.5"
          onPointerDown={stopBubble}
        >
          {STICKY_COLORS.map((color) => (
            <button
              key={color}
              type="button"
              aria-label={`Color ${color}`}
              aria-pressed={element.color === color}
              className={cn(
                "h-3.5 w-3.5 shrink-0 rounded-full border-2 transition-colors",
                element.color === color
                  ? "border-[var(--t-accent)]"
                  : "border-black/10 hover:border-black/20",
              )}
              style={{ backgroundColor: STICKY_BG_SOLID[color] }}
              onPointerDown={stopBubble}
              onClick={(event) => {
                event.stopPropagation();
                onChange(element.id, { color });
              }}
            />
          ))}
          <button
            type="button"
            aria-label="Delete sticky"
            className="ml-auto inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-[var(--t-event-pill-radius)] text-[var(--t-meta)] transition-colors hover:bg-red-50 hover:text-red-600"
            onPointerDown={stopBubble}
            onClick={(event) => {
              event.stopPropagation();
              onDelete(element.id);
            }}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : null}
      <textarea
        ref={textareaRef}
        value={element.text || ""}
        onChange={(e) => onChange(element.id, { text: e.target.value })}
        onPointerDown={(e) => {
          e.stopPropagation();
          onSelect(element.id);
        }}
        placeholder="Write something…"
        className="relative z-[1] min-h-0 flex-1 resize-none bg-transparent p-3 text-sm font-light text-[var(--t-text)] outline-none placeholder:text-[var(--t-meta)]"
      />
    </div>
  );
}
